import { type SchedulePushKey, schedulePushKey } from "./schedulePushConsts";
import { AOD_PREVIEW_ACTION_KEYS, AOD_PREVIEW_SKIP_KEYS, PREVIEW_ACTION_KEYS, PREVIEW_SKIP_KEYS } from "./schedulePushLabels";
import { SchedulePushSource } from "./schedulePushTypes";

/**
 * Everything the panel says or marks differently for one vendor — the one place a vendor differs, so the section,
 * the card, the dialogs and the preview are written once and a new vendor is a new entry here rather than a fork.
 */
export type SchedulePushVendorProfile = {
	/** The stem of every test id in the vendor's section (`schedule-push-status-card`, `schedule-push-aod-status-card`). */
	testIdStem: string;
	/**
	 * Shared strings this vendor words in its own terms, as shared key → the vendor's key (under its source). The
	 * panel's `t` swaps them in, so a shared component keeps asking for `save.submit` and HostedTime keeps reading
	 * "Save mapping" while Attendance on Demand reads "Save Department ID".
	 */
	copy: Readonly<Partial<Record<SchedulePushKey, SchedulePushKey>>>;
	previewActionKeys: Readonly<Record<string, SchedulePushKey>>;
	previewSkipKeys: Readonly<Record<string, SchedulePushKey>>;
	/** The example of the vendor's own code format in the facility code field. */
	facilityCodePlaceholderKey: SchedulePushKey;
	/** What starting changes in the vendor beyond the confirmation's body, one line each; empty when the body says it all. */
	startConsequenceKeys: readonly SchedulePushKey[];
	/** A line the preview result opens with, for a vendor whose counts need framing; undefined when they don't. */
	previewScopeNoteKey: SchedulePushKey | undefined;
};

const HOSTED_TIME: SchedulePushVendorProfile = {
	testIdStem: "schedule-push",
	copy: {},
	previewActionKeys: PREVIEW_ACTION_KEYS,
	previewSkipKeys: PREVIEW_SKIP_KEYS,
	facilityCodePlaceholderKey: schedulePushKey("hostdime.facilityCode.placeholder"),
	startConsequenceKeys: [],
	previewScopeNoteKey: undefined,
};

/** Shared key → Attendance on Demand's own wording: its key is a Department ID with no map, and it never writes the past. */
const AOD_COPY: Readonly<Partial<Record<SchedulePushKey, SchedulePushKey>>> = {
	[schedulePushKey("section.description")]: schedulePushKey("aod.section.description"),
	[schedulePushKey("hero.notSetUp.description")]: schedulePushKey("aod.hero.notSetUp.description"),
	[schedulePushKey("hero.on.description")]: schedulePushKey("aod.hero.on.description"),
	[schedulePushKey("hero.off.description")]: schedulePushKey("aod.hero.off.description"),
	[schedulePushKey("facilityCode.label")]: schedulePushKey("aod.facilityCode.label"),
	[schedulePushKey("facilityCode.description")]: schedulePushKey("aod.facilityCode.description"),
	[schedulePushKey("facilityCode.prefilled")]: schedulePushKey("aod.facilityCode.prefilled"),
	[schedulePushKey("facilityCode.locked.sent")]: schedulePushKey("aod.facilityCode.locked.sent"),
	[schedulePushKey("save.region")]: schedulePushKey("aod.save.region"),
	[schedulePushKey("save.hint.first")]: schedulePushKey("aod.save.hint.first"),
	[schedulePushKey("save.hint.off")]: schedulePushKey("aod.save.hint.off"),
	[schedulePushKey("save.submit")]: schedulePushKey("aod.save.submit"),
	[schedulePushKey("save.success")]: schedulePushKey("aod.save.success"),
	[schedulePushKey("save.failed")]: schedulePushKey("aod.save.failed"),
	[schedulePushKey("save.blocked.noCode")]: schedulePushKey("aod.save.blocked.noCode"),
	[schedulePushKey("save.blocked.codeRefused")]: schedulePushKey("aod.save.blocked.codeRefused"),
	[schedulePushKey("blocked.notSaved")]: schedulePushKey("aod.blocked.notSaved"),
	[schedulePushKey("blocked.login")]: schedulePushKey("aod.blocked.login"),
	[schedulePushKey("blocked.loginPartial")]: schedulePushKey("aod.blocked.loginPartial"),
	[schedulePushKey("preview.blocked.notSaved")]: schedulePushKey("aod.blocked.notSaved"),
	[schedulePushKey("preview.blocked.unsaved")]: schedulePushKey("aod.preview.blocked.unsaved"),
	[schedulePushKey("start.blocked.mapDirty")]: schedulePushKey("aod.start.blocked.codeDirty"),
	[schedulePushKey("toggle.on.body")]: schedulePushKey("aod.toggle.on.body"),
	[schedulePushKey("toggle.off.body")]: schedulePushKey("aod.toggle.off.body"),
	[schedulePushKey("preview.intro")]: schedulePushKey("aod.preview.intro"),
	[schedulePushKey("preview.running.expectation")]: schedulePushKey("aod.preview.running.expectation"),
	[schedulePushKey("preview.stat.writes")]: schedulePushKey("aod.preview.stat.writes"),
	[schedulePushKey("preview.stat.unchanged")]: schedulePushKey("aod.preview.stat.unchanged"),
	[schedulePushKey("preview.stat.skipped")]: schedulePushKey("aod.preview.stat.skipped"),
	[schedulePushKey("preview.skips.title")]: schedulePushKey("aod.preview.skips.title"),
	[schedulePushKey("preview.skips.empty")]: schedulePushKey("aod.preview.skips.empty"),
	[schedulePushKey("preview.capNote")]: schedulePushKey("aod.preview.capNote"),
};

const ATTENDANCE_ON_DEMAND: SchedulePushVendorProfile = {
	testIdStem: "schedule-push-aod",
	copy: AOD_COPY,
	previewActionKeys: AOD_PREVIEW_ACTION_KEYS,
	previewSkipKeys: AOD_PREVIEW_SKIP_KEYS,
	facilityCodePlaceholderKey: schedulePushKey("aod.facilityCode.placeholder"),
	startConsequenceKeys: [
		schedulePushKey("aod.toggle.on.consequence.wholeDay"),
		schedulePushKey("aod.toggle.on.consequence.leftAlone"),
		schedulePushKey("aod.toggle.on.consequence.noPast"),
		schedulePushKey("aod.toggle.on.consequence.noUndo"),
	],
	previewScopeNoteKey: schedulePushKey("aod.preview.scopeNote"),
};

export const SCHEDULE_PUSH_VENDORS: Readonly<Record<SchedulePushSource, SchedulePushVendorProfile>> = {
	[SchedulePushSource.HostedTime]: HOSTED_TIME,
	[SchedulePushSource.AttendanceOnDemand]: ATTENDANCE_ON_DEMAND,
};
